import React, { useState } from 'react';
import { Box, Button, Dialog, IconButton, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Close } from '@material-ui/icons';
import { AddressAutoComplete, AddressAutoCompleteType } from '../../../../../common/inputs/AddressAutoComplete';
import { AlertCard, getAlertCardColors } from '../../../../../common/cards/alert-card/AlertCard';
import { GeocodeStatus, PlacesAddress } from '../activity-import-types';
import { getPrimaryButtonStyle, getToolbarButtonStyle } from '../import-button-styles';

export interface LocationSelection {
  address: PlacesAddress | null;
  remoteDetails: string;
}

type LocationType = 'local' | 'remote';

const EMPTY_SELECTION: LocationSelection = { address: null, remoteDetails: '' };

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '20px 24px 0',
  },
  title: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#333333',
  },
  subtitle: {
    fontSize: '13px',
    color: theme.color.grey.neutral300,
    marginTop: 4,
  },
  body: {
    padding: '20px 24px',
    '& > * + *': {
      marginTop: 16,
    },
  },
  toggle: {
    display: 'inline-flex',
    border: '1px solid #D9D9D9',
    borderRadius: 4,
    overflow: 'hidden',
  },
  toggleOption: {
    fontSize: '12px',
    fontWeight: 600,
    padding: '6px 14px',
    border: 'none',
    background: 'transparent',
    color: theme.color.grey.neutral300,
    cursor: 'pointer',
    '& + &': {
      borderLeft: '1px solid #D9D9D9',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.color.secondary.main900}`,
      outlineOffset: -2,
    },
  },
  toggleSelected: {
    backgroundColor: theme.color.secondary.main900,
    color: '#ffffff',
  },
  label: {
    fontSize: '12px',
    color: theme.color.grey.neutral300,
    marginBottom: 4,
    display: 'block',
  },
  selected: {
    fontSize: '13px',
    color: '#333333',
    marginTop: 8,
  },
  textarea: {
    font: 'inherit',
    fontSize: '13px',
    color: '#333333',
    padding: '8px 10px',
    border: '1px solid #D9D9D9',
    borderRadius: 4,
    width: '100%',
    boxSizing: 'border-box',
    resize: 'vertical',
    '&:focus': {
      outline: 'none',
      borderColor: theme.color.secondary.main900,
      boxShadow: `0 0 0 3px ${theme.color.secondary.main50}`,
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 24px 20px',
    '& > * + *': {
      marginLeft: 10,
    },
  },
  button: getToolbarButtonStyle(theme),
  primaryButton: getPrimaryButtonStyle(theme),
}));

interface Props {
  open: boolean;
  activityName: string;
  importedAddress: string;
  resolvedAddress: PlacesAddress | null;
  geocodeStatus: GeocodeStatus;
  remoteDetails: string;
  onClose: () => void;
  onSave: (selection: LocationSelection) => void;
}

export function AddressCellDialog({
  open,
  activityName,
  importedAddress,
  resolvedAddress,
  geocodeStatus,
  remoteDetails: initialRemoteDetails,
  onClose,
  onSave,
}: Props) {
  const classes = useStyles();
  const [locationType, setLocationType] = useState<LocationType>(
    initialRemoteDetails && !importedAddress ? 'remote' : 'local'
  );
  const [selected, setSelected] = useState<AddressAutoCompleteType | undefined>(
    resolvedAddress ? { ...resolvedAddress, placesId: resolvedAddress.placesId ?? null } : undefined
  );
  const [remoteDetails, setRemoteDetails] = useState(initialRemoteDetails);
  const alertColors = getAlertCardColors('info');

  const canSave = locationType === 'local' ? Boolean(selected) : Boolean(remoteDetails.trim());

  const handleSave = () => {
    if (locationType === 'remote') {
      onSave({ address: null, remoteDetails: remoteDetails.trim() });
      return;
    }
    if (!selected) return;
    onSave({
      address: {
        description: selected.description,
        formatted: selected.formatted,
        placesId: selected.placesId ?? undefined,
        latitude: Number(selected.latitude),
        longitude: Number(selected.longitude),
      },
      remoteDetails: '',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <Box className={classes.header}>
        <Box flex={1}>
          <Typography className={classes.title}>Location</Typography>
          <Typography className={classes.subtitle}>{activityName || 'Untitled activity'}</Typography>
        </Box>
        <IconButton size='small' onClick={onClose} aria-label='Close'>
          <Close fontSize='small' />
        </IconButton>
      </Box>

      <Box className={classes.body}>
        <div className={classes.toggle} role='radiogroup' aria-label='Location type'>
          {(['local', 'remote'] as LocationType[]).map((type) => (
            <button
              key={type}
              type='button'
              role='radio'
              aria-checked={locationType === type}
              className={`${classes.toggleOption} ${locationType === type ? classes.toggleSelected : ''}`}
              onClick={() => setLocationType(type)}
            >
              {type === 'local' ? 'Local' : 'Remote'}
            </button>
          ))}
        </div>

        {locationType === 'local' ? (
          <Box>
            {importedAddress && !resolvedAddress && (
              <Box mb={2}>
                <AlertCard
                  variant='info'
                  title={
                    geocodeStatus === 'pending'
                      ? "We're still looking up this address"
                      : "We couldn't match this address"
                  }
                >
                  <Typography style={{ fontSize: '13px', color: alertColors.darkTextColor }}>
                    Your file says &quot;{importedAddress}&quot;. Search for it below and pick the right match.
                  </Typography>
                </AlertCard>
              </Box>
            )}
            <Typography component='label' htmlFor='activity-import-address' className={classes.label}>
              Search for an address
            </Typography>
            <AddressAutoComplete id='activity-import-address' value={selected} onChange={setSelected} />
            {selected && <Typography className={classes.selected}>Selected: {selected.formatted}</Typography>}
          </Box>
        ) : (
          <Box>
            <Typography component='label' htmlFor='activity-import-remote' className={classes.label}>
              Additional info (URL, phone number, etc.)
            </Typography>
            <textarea
              id='activity-import-remote'
              className={classes.textarea}
              rows={3}
              value={remoteDetails}
              onChange={(e) => setRemoteDetails(e.target.value)}
            />
          </Box>
        )}
      </Box>

      <Box className={classes.footer}>
        <Button className={classes.button} style={{ marginRight: 'auto' }} onClick={() => onSave(EMPTY_SELECTION)}>
          Clear location
        </Button>
        <Button className={classes.button} onClick={onClose}>
          Cancel
        </Button>
        <Button className={classes.primaryButton} disabled={!canSave} onClick={handleSave}>
          Save
        </Button>
      </Box>
    </Dialog>
  );
}
